import { useQuery } from "react-query";
import { QueryKeys } from "../common/queryKeys";
import { musicService } from "../api/musicService";
import { listingService } from "../api/listingService";
import { bidService } from "../api/bidService";
import {
  IApiResponse,
  IBid,
  IListing,
  IMusic,
  INft,
  IQueryParams,
} from "../common/commonTypes";
import { nftService } from "../api/nftService";
import { useRouter } from "next/router";
import { routes } from "../routes";

interface IAssetInfo {
  music: IMusic;
  listing?: IListing;
  bids?: IBid[];
  nft?: INft;
}

const fetchBids = async (listingId: string): Promise<IBid[]> => {
  const bidResponse = await bidService.fetchBidsForListing(listingId);
  return bidResponse.data.items;
};

const fetchConnectedListing = async (
  musicId: string,
  listingId?: string
): Promise<IListing | undefined> => {
  if (listingId) {
    const listingResponse = await listingService.fetchListing(listingId);
    return listingResponse.data;
  }

  const onSaleListingResponse = await listingService.fetchListings({
    where: {
      music_id: musicId,
      status: "active",
    },
    order: [["id", "DESC"]],
    limit: 1,
  });

  if (onSaleListingResponse.data.count > 0) {
    return onSaleListingResponse.data.items[0];
  }

  const listingResponse = await listingService.fetchListings({
    where: {
      music_id: musicId,
    },
    order: [["id", "DESC"]],
    limit: 1,
  });

  return listingResponse.data.items[0];
};

export const fetchAssetInfo = async (
  musicId: string,
  listingId?: string
): Promise<IAssetInfo> => {
  const musicResponse = await musicService.fetchMusic(musicId, {});
  const music = musicResponse.data;
  const listing = await fetchConnectedListing(musicId, listingId);

  if (listing) {
    const [bids, nft] = await Promise.all([
      fetchBids(listing.id),
      nftService.fetchNftForListing(listing.id),
    ]);

    return {
      music,
      listing,
      bids,
      nft,
    };
  }

  return {
    music,
    listing,
  };
};

export const useAssetDetailsQuery = (musicId: string, listingId?: string) => {
  const router = useRouter();
  return useQuery<IAssetInfo, IApiResponse>(
    [QueryKeys.assetDetails, { musicId, listingId }],
    () => fetchAssetInfo(musicId, listingId),
    {
      onError: (error) => {
        if (error.status && [400, 404].includes(error.status)) {
          router.replace(routes.page404);
          return;
        }
      },
    }
  );
};

export const useRelatedTracksQuery = (
  music: IMusic,
  enabled: boolean = true
) => {
  return useQuery<IMusic[]>(
    [QueryKeys.assetRelatedTracks, music.id],
    async () => {
      const response = await musicService.fetchMusics({
        where: {
          $and: [
            { artist_id: music.artist_id },
            { "$artist.approved_artist$": { $eq: true } },
            { $not: [{ id: music.id }] },
          ],
        },
      });
      return response.data.items;
    },
    { enabled }
  );
};

export const useRelatedArtistsQuery = (music: IMusic) => {
  const queryParams: IQueryParams = {
    where: {
      $or: [],
      $and: [
        { "$artist.approved_artist$": { $eq: true } },
        { $not: [{ artist_id: music.artist_id }] },
      ],
    },
  };

  music?.countries?.forEach((country) => {
    queryParams.where.$or.push({ countries: { $contains: [country] } });
  });
  music?.languages?.forEach((lang) => {
    queryParams.where.$or.push({ languages: { $contains: [lang] } });
  });
  music?.genres?.forEach((genre) => {
    queryParams.where.$or.push({ genres: { $contains: [genre] } });
  });

  return useQuery<IMusic[]>(
    [QueryKeys.assetRelatedArtists, music.id],
    async () => {
      const response = await musicService.fetchMusics(queryParams);
      return response.data.items.filter(
        (item: IMusic, index: number, arr: IMusic[]) => {
          return (
            arr.map((_item) => _item.artist?.id).indexOf(item.artist?.id) ===
            index
          );
        }
      );
    }
  );
};
